import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    DialogV2,
    FlashMessage,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from 'c-components';
import apiClient from 'c-data/apiClient';
import { useCommonTranslation } from 'c-translation';
import { useAPIClientRequest, useAutoResetBoolean } from 'c-hooks';
import {
    Box,
    CircularProgress,
    IconButton,
    Stack,
    Table,
    TableContainer,
    Tooltip,
    Typography,
} from '@mui/material';
import to from 'await-to-js';
import { ContentCopy } from '@mui/icons-material';
import { IfUserIsOneOfTypes } from 'c-auth-module/Components';
import type { Campaign } from 'c-sdk';
import { UserType } from 'c-sdk';

import { copyTextToClipboard } from 'c-lib';
import DeleteIcon from '@mui/icons-material/Delete';

type Props = {
    open: boolean;
    onClose: () => void;
    campaign: Campaign;
};

const CampaignShareDialog: React.FC<Props> = ({ campaign, open, onClose }) => {
    const campaignId = useMemo(() => campaign.id, [campaign.id]);
    const { start, data, isLoading, error, hasFailed, hasSucceeded } = useAPIClientRequest(
        apiClient.Entities.Campaign.shareUrl,
    );
    const fetchShareLink = useCallback(
        async (type: string) => {
            await to(start(campaignId, type));
        },
        [campaignId, start],
    );
    const t = useCommonTranslation();

    return (
        <DialogV2
            open={open}
            onClose={onClose}
            title="Modules.Main.Campaigns.Overview.ActionLabels.share"
            MuiDialogProps={{ id: 'campaign-share-dialog' }}
            maxWidth="md"
        >
            <>
                <Box display="flex" justifyContent="center">
                    {hasFailed && <FlashMessage status="error">{error}</FlashMessage>}
                    <Stack gap={2}>
                        <Stack direction="row" spacing={2} display="flex" justifyContent="center">
                            <Button disabled={isLoading} onClick={() => fetchShareLink('agency')}>
                                {t(
                                    'Modules.Main.Campaigns.Overview.ShareDialog.generateAgencyLink',
                                )}
                            </Button>
                            <Button
                                disabled={isLoading}
                                onClick={() => fetchShareLink('reporting')}
                            >
                                {t(
                                    'Modules.Main.Campaigns.Overview.ShareDialog.generateReportingLink',
                                )}
                            </Button>
                        </Stack>
                        {isLoading && <CircularProgress />}
                        {hasSucceeded && (
                            <Stack spacing={2}>
                                <CopyableShareLink url={data?.data?.url} />
                            </Stack>
                        )}
                    </Stack>
                </Box>
                <IfUserIsOneOfTypes roles={[UserType.Admin]}>
                    {open && <SharedWithUsers campaign={campaign} />}
                </IfUserIsOneOfTypes>
            </>
        </DialogV2>
    );
};

const CopyableShareLink: React.FC<{ url: string }> = ({ url }) => {
    const { setTrue, value: CopySuccess } = useAutoResetBoolean(false, 2500);
    const onCopy = useCallback(async () => {
        const [err] = await to(copyTextToClipboard(url));

        if (!err) setTrue();
    }, [url, setTrue]);

    const t = useCommonTranslation();

    return (
        <Stack>
            <Box display="flex" flexWrap="nowrap" alignItems="center" justifyContent="center">
                <Typography
                    variant="body1"
                    align="center"
                    whiteSpace="pre-line"
                    sx={{ wordBreak: 'break-all' }}
                >
                    {url}
                </Typography>
                <Tooltip
                    title={t('Modules.Main.Campaigns.Overview.ShareDialog.shareCopied')}
                    open={CopySuccess}
                >
                    <IconButton
                        onClick={onCopy}
                        size="medium"
                        color={CopySuccess ? 'success' : undefined}
                    >
                        <ContentCopy fontSize="inherit" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Stack>
    );
};

const SharedWithUsers: React.FC<{ campaign: Campaign }> = ({ campaign }) => {
    const t = useCommonTranslation();
    const [sharedUsers, setSharedUsers] = useState([]);

    const { start, reset, isLoading, error } = useAPIClientRequest(
        apiClient.Entities.Campaign.unshareUrl,
    );

    useEffect(() => {
        if (campaign?.campaignUsers) {
            const mapped = campaign.campaignUsers.map(cu => {
                const user = cu.user;
                const fullName = `${user.name_first} ${user.name_last}`.trim();
                const type =
                    cu.link_type === 'agency'
                        ? 'Agency Link'
                        : cu.link_type === 'reporting'
                        ? 'Reporting Link'
                        : 'Admin';

                return {
                    userId: cu.user_id,
                    email: user.email,
                    name: fullName,
                    type,
                    date: cu?.created_at?.split('T')[0],
                    linkType: cu.link_type,
                };
            });

            setSharedUsers(mapped);
        }
    }, [campaign]);

    const sharedWithTitle = useMemo(
        () =>
            t('Modules.Main.Campaigns.Overview.ShareDialog.sharedWithUsers', {
                count: sharedUsers.length,
            }),
        [t, sharedUsers.length],
    );

    const handleDelete = useCallback(
        async (userId: number, link_type: string) => {
            const [err, succ] = await to(start(campaign.id, { user_id: userId, link_type }));
            if (!err && succ) {
                setSharedUsers(prev =>
                    prev.filter(user => !(user.userId === userId && user.linkType === link_type)),
                );
            }
        },
        [campaign.id, start],
    );

    return (
        <>
            <Box>
                <Stack justifyContent="center" alignItems="center">
                    <Typography variant="h4" align="center">
                        {sharedWithTitle}
                    </Typography>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <strong>
                                            {t(
                                                'Modules.Main.Campaigns.Overview.ShareDialog.table.user',
                                            )}
                                        </strong>
                                    </TableCell>
                                    <TableCell>
                                        <strong>
                                            {t(
                                                'Modules.Main.Campaigns.Overview.ShareDialog.table.via',
                                            )}
                                        </strong>
                                    </TableCell>
                                    <TableCell>
                                        <strong>
                                            {t(
                                                'Modules.Main.Campaigns.Overview.ShareDialog.table.associationDate',
                                            )}
                                        </strong>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sharedUsers.map(user => (
                                    <TableRow key={`${user.userId}-${user.linkType}`}>
                                        <TableCell>
                                            <Typography variant="body2">{user.name}</Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {user.email}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{user.type}</TableCell>
                                        <TableCell>
                                            <Stack direction="row" gap={2}>
                                                {user.date}
                                                <Tooltip title="Remove">
                                                    <IconButton
                                                        onClick={() =>
                                                            handleDelete(
                                                                user.userId as number,
                                                                user.linkType,
                                                            )
                                                        }
                                                        size="small"
                                                        disabled={isLoading}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {isLoading && (
                        <Box display="flex" justifyContent="center" mt={2}>
                            <CircularProgress />
                        </Box>
                    )}
                    {error && (
                        <Alert severity="error" onClose={reset}>
                            {String(error)}
                        </Alert>
                    )}
                </Stack>
            </Box>
        </>
    );
};

export default CampaignShareDialog;
